import { type FC, type MouseEventHandler } from 'react'
import cn from 'classnames'
import { Capacitor } from '@capacitor/core'
import { twMerge } from 'tailwind-merge'
import { ChevronLeftIcon } from '@heroicons/react/24/outline'
import { useNavigate } from 'react-router'
import { type DefaultProps } from '../common'
import { MenuButton } from '../menu-button/menu-button'

export interface HeaderCoverProps extends DefaultProps {
  /**
   * HeaderCover category
   */
  category: string

  /**
   * HeaderCover title
   */
  title: string

  /**
   * HeaderCover image URL
   */
  imgUrl: string

  /**
   * Click handler
   */
  onClick?: MouseEventHandler<HTMLButtonElement>

  /**
   * backLinkUrl
   * Follows Implementation of backLinkUrl in HeaderCover, which allows for Mobile Navigation to pick up the backLinkUrl and navigate back to the previous page.
   */
  backLinkUrl?: string
}

export const HeaderCover: FC<HeaderCoverProps> = ({
  // Custom component properties
  onClick,
  imgUrl,
  title,
  category,
  backLinkUrl,
  // Common default properties for each component
  className,
  id,
  testId,
  ariaLabel,
}) => {
  // Component base class names. Can be overriden by className property.
  const baseClass = cn(
    'flex flex-col justify-end bg-top-left bg-cover bg-[center_left] md:bg-[center_center] min-h-[200px] md:min-h-[240px]',
    {
      'pt-md': Capacitor.isNativePlatform(),
    },
  )

  // Final class names
  const finalClasses = twMerge(baseClass, className)

  const navigate = useNavigate()

  return (
    <div
      style={{
        backgroundImage: `url(${imgUrl}`,
      }}
      className={finalClasses}
      {...(testId ? { 'data-testid': testId } : {})}
      {...(id ? { id } : {})}
      {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
    >
      {backLinkUrl ? (
        <button
          className="hidden md:block relative top-[4.75rem] left-[calc(12%)] border border-white rounded-full p-2 group cursor-pointer hover:bg-white hover:shadow hover:border-white border-iconmuted flex items-center justify-center w-fit h-fit"
          id="back-link-page-header"
          data-back-link-url={backLinkUrl}
          data-testid="header-cover-back-arrow"
          type="button"
          onClick={() => {
            void navigate(backLinkUrl)
          }}
        >
          <ChevronLeftIcon id="back_link_icon" className="text-white group-hover:text-iconmuted h-5 w-5" />
        </button>
      ) : null}
      <div className="egw-container pb-0 flex flex-row justify-between">
        <div className="pb-12 md:pb-16">
          <p data-testid="header-cover-category" className="text-xl font-bold text-white">
            {category}
          </p>
          <p
            data-testid="header-cover-title"
            className="text-4xl lg:text-5xl font-light leading-tight md:leading-none md:pb-[0.375rem] text-white sm:line-clamp-3 xs:line-clamp-2 min-md:line-clamp-none"
          >
            {title}
          </p>
        </div>
        {onClick ? <MenuButton testId="header-cover-menu" onClick={onClick} /> : null}
      </div>
    </div>
  )
}
